import React from 'react';
import { Link } from 'react-router-dom';
import { User, Buildings, Phone, EnvelopeSimple } from '@phosphor-icons/react';
import { ResourceType } from 'shared/types/resource';
import { ResourceValue } from './ResourceSearchSelect';

interface ResourceDisplayProps {
    value: ResourceValue | null;
    linkToDetail?: boolean;
}

export const ResourceDisplay: React.FC<ResourceDisplayProps> = ({
    value,
    linkToDetail = true
}) => {
    if (!value || !value.name) {
        return <p className="text-neutral-400 italic text-sm">Niet ingevuld</p>;
    }

    const isContact = value.type === ResourceType.CONTACT;
    const detailUrl = `/${value.type}/${value.resourceId}`;

    const content = (
        <div className="bg-white border border-neutral-primary/20 rounded-lg overflow-hidden">
            {/* Header with name and type */}
            <div className="flex items-center gap-3 px-3 py-2.5 bg-neutral-50/50 border-b border-neutral-100">
                <div className="flex-shrink-0">
                    {isContact ? (
                        <div className="w-8 h-8 rounded-full bg-moss-primary/10 flex items-center justify-center">
                            <User className="h-4 w-4 text-moss-primary" weight="fill" />
                        </div>
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                            <Buildings className="h-4 w-4 text-sky-600" weight="fill" />
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`font-medium ${linkToDetail ? 'text-moss-primary hover:underline' : 'text-neutral-900'}`}>
                        {value.name}
                    </div>
                    {isContact && value.functionTitle && (
                        <div className="text-xs text-neutral-500">{value.functionTitle}</div>
                    )}
                </div>
            </div>
            {/* Details section */}
            <div className="px-3 py-2 space-y-1.5">
                {/* Company for contacts */}
                {isContact && (
                    <div className="flex items-center gap-2 text-sm">
                        <Buildings className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                        <span className={value.companyName ? "text-neutral-700" : "text-neutral-400 italic"}>
                            {value.companyName || "Geen bedrijf"}
                        </span>
                    </div>
                )}
                {/* Phone */}
                <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                    <span className={value.phone ? "text-neutral-700" : "text-neutral-400 italic"}>
                        {value.phone || "Geen telefoon"}
                    </span>
                </div>
                {/* Email */}
                <div className="flex items-center gap-2 text-sm">
                    <EnvelopeSimple className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                    <span className={value.email ? "text-neutral-700 truncate" : "text-neutral-400 italic"}>
                        {value.email || "Geen e-mail"}
                    </span>
                </div>
            </div>
        </div>
    );

    if (linkToDetail && value.resourceId) {
        return (
            <Link to={detailUrl} className="block hover:opacity-90 transition-opacity">
                {content}
            </Link>
        );
    }

    return content;
};

export default ResourceDisplay;
